import React from "react";
import Section from "../Section";
import {
    FaUserShield,
    FaInfoCircle,
    FaFileAlt,
    FaLock,
    FaRegClock,
    FaUserCheck,
    FaCookieBite,
    FaChild,
    FaMobileAlt,
    FaMoneyBillWave,
    FaSyncAlt,
    FaEnvelope,
    FaPhone,
    FaAddressBook,
} from "react-icons/fa";
import { pageTitle } from "../../helpers/PageTitle";
const PrivacyPolicy = () => {
    const iconStyle = { color: "#aad1f0", marginRight: "8px" };

    const SectionCard = ({ icon, title, children }) => (
        <div className="card shadow p-4 mb-4 border-0 rounded-4">
            <h4 className="mb-3">
                {icon} {title}
            </h4>
            {children}
        </div>
    );

    const Table = ({ headers, rows }) => (
        <div className="table-responsive shadow-sm rounded-4">
            <table className="table table-bordered border-light-subtle mb-0">
                <thead>
                    <tr>
                        {headers.map((header, idx) => (
                            <th
                                key={idx}
                                style={{
                                    backgroundColor: "#aad1f0",
                                    color: "#000",
                                }}
                            >
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <tr key={idx}>
                            {row.map((cell, cellIdx) => (
                                <td key={cellIdx}>{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    pageTitle("Privacy Policies");

    return (<>
        <Section
            topMd={185}
            topLg={140}
            topXl={100}
            bottomMd={200}
            bottomLg={150}
            bottomXl={110}
        >
            <div className="container py-5">
                <h1 className="mb-5 text-center" style={{marginTop:"70px"}}>Privacy Policies</h1>

                <SectionCard
                    icon={<FaInfoCircle style={iconStyle} />}
                    title="Introduction"
                >
                    <p>
                        GKC Klinica ("we," "us," or "our") respects your privacy
                        and is committed to protecting your personal
                        information. This Privacy Policies explains what
                        personal data we collect, why we collect it, how it is
                        used, with whom it may be shared, and how it is
                        protected when you use our services via our website (
                        <a
                            href="https://www.gkcklinica.com"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            www.gkcklinica.com
                        </a>
                        ) and mobile application (collectively, the “Platform”).
                    </p>
                    <p>
                        By accessing or using our Platform, you agree to this
                        Privacy Policies and the applicable data protection laws
                        of India.
                    </p>
                </SectionCard>

                <SectionCard
                    icon={<FaUserShield style={iconStyle} />}
                    title="Information We Collect"
                >
                    <h6>a. Personal Information (Type and Purpose)</h6>
                    <p>
                        We collect the following personal data for the purpose
                        of service delivery, communication, and legal
                        compliance:
                    </p>
                    <Table
                        headers={[
                            "Type of Personal Information",
                            "Purpose of Collection",
                        ]}
                        rows={[
                            [
                                "Name, Email, Phone Number, Date of Birth, Gender",
                                "User identification and communication",
                            ],
                            [
                                "Address and PIN Code",
                                "To locate nearest services, labs, or clinics",
                            ],
                            [
                                "Aadhaar Number (optional)",
                                "Identity verification, if required by law",
                            ],
                            [
                                "Billing and Payment Details",
                                "To process payments for memberships, checkups, or consultations",
                            ],
                            [
                                "Profile Photo (optional)",
                                "Personalization of user profile",
                            ],
                            [
                                "Uploaded Insurance/Medical Documents",
                                "For digital storage, sharing with healthcare/insurance providers",
                            ],
                        ]}
                    />

                    <h6 className="mt-4">b. Sensitive Personal Data</h6>
                    <ul>
                        <li>Medical history, prescriptions, doctor notes</li>
                        <li>Test results and consultation records</li>
                        <li>Insurance policy details</li>
                    </ul>
                    <p>
                        <strong>Purpose:</strong> These are collected to provide
                        healthcare consultations, diagnostics, and
                        insurance-related features
                    </p>

                    <h6 className="mt-4">c. Technical & Usage Data</h6>
                    <ul>
                        <li>IP address, device type, OS, browser, device ID</li>
                        <li>Location (with permission)</li>
                        <li>App/website usage patterns</li>
                    </ul>
                    <p>
                        <strong>Purpose:</strong> Improve platform performance,
                        detect fraud, and personalize experience.
                    </p>

                    <h6 className="mt-4">d. Audio & Visual Data</h6>
                    <ul>
                        <li>Audio/video from online consultations</li>
                        <li>Uploaded photos/documents</li>
                    </ul>
                    <p>
                        <strong>Purpose:</strong> For telemedicine services and
                        medical record storage
                    </p>
                </SectionCard>

                <SectionCard className="mb-5">
                    <h4>
                        <FaLock style={iconStyle} />
                        How We Use Your Information{" "}
                    </h4>
                    <p>We use your data for the following purposes:</p>
                    <ul>
                        <li>
                            Provide health checkups, diagnostics, and
                            consultations
                        </li>
                        <li>Process membership plans and payment history</li>
                        <li>Enable document upload, sharing, and storage</li>
                        <li>Maintain and protect electronic medical records</li>
                        <li>
                            Send alerts, updates, and appointment confirmations
                        </li>
                        <li>Improve and personalize user experience</li>
                        <li>Send promotional messages (with consent)</li>
                        <li>Comply with legal requirements</li>
                    </ul>
                </SectionCard>

                <SectionCard
                    icon={<FaSyncAlt style={iconStyle} />}
                    title="Sharing Your Information"
                >
                    <p>
                        We do not sell your personal data. Your information may
                        be shared only under strict confidentiality with:
                    </p>
                    <Table
                        headers={["Shared With", "Purpose"]}
                        rows={[
                            [
                                "Healthcare professionals",
                                "To facilitate consultations, checkups, and treatment",
                            ],
                            [
                                "Insurance partners",
                                "To manage and verify uploaded policies (with user consent)",
                            ],
                            [
                                "Third-party service providers",
                                "For secure payment processing, cloud storage, analytics, and support",
                            ],
                            [
                                "Government/law enforcement agencies",
                                "Only if required by applicable laws, regulations, or legal processes",
                            ],
                            [
                                "Business successors",
                                "In case of mergers, acquisitions, or restructuring, with adequate safeguards",
                            ],
                        ]}
                    />
                </SectionCard>

                {/* 5. Data Security */}
                <SectionCard className="mb-5">
                    <h4>
                        <FaLock style={iconStyle} /> Data Security
                    </h4>
                    <p>
                        We implement multiple layers of security to protect your
                        data:
                    </p>
                    <ul>
                        <li>
                            Encryption of health and financial data (in transit
                            and at rest)
                        </li>
                        <li>Role-based access controls for internal staff</li>
                        <li>
                            Secure cloud infrastructure with industry-standard
                            compliance
                        </li>
                        <li>Audit logs and activity monitoring</li>
                    </ul>
                    <p>
                        However, while we use best-in-class technologies, no
                        method of online transmission is 100% secure. Users are
                        encouraged to protect their login credentials.
                    </p>
                </SectionCard>

                {/* 6. Data Retention */}
                <SectionCard className="mb-5">
                    <h4>
                        <FaRegClock style={iconStyle} /> Retention of
                        Information
                    </h4>
                    <p>We retain user data:</p>
                    <ul>
                        <li>For as long as your account is active, or</li>
                        <li>
                            For a minimum of 3 years from your last activity, to
                            comply with healthcare regulations, tax, and legal
                            requirements.
                        </li>
                    </ul>
                </SectionCard>

                {/* 7. User Rights */}
                <SectionCard className="mb-5">
                    <h4>
                        <FaUserCheck style={iconStyle} /> Your Rights and
                        Choices
                    </h4>
                    <ul>
                        <li>
                            Access, update, or delete your personal information
                        </li>
                        <li>
                            Withdraw consent (may affect service availability)
                        </li>
                        <li>Opt-out of promotional communications</li>
                        <li>Request data export (in applicable formats)</li>
                    </ul>
                    <p>
                        Email us at:{" "}
                        <a href="mailto:support@gkcklinica.com">
                            support@gkcklinica.com
                        </a>
                    </p>
                </SectionCard>

                {/* 8. Cookies */}
                <SectionCard className="mb-5">
                    <h4>
                        <FaCookieBite style={iconStyle} />
                        Cookies and Tracking Technologies
                    </h4>
                    <p>We use cookies and tracking tools to:</p>
                    <ul>
                        <li>Analyze user behavior</li>
                        <li>Save preferences and session info</li>
                        <li>Improve user experience</li>
                    </ul>
                    <p>
                        You may control cookie permissions via browser settings.
                        Disabling cookies may affect Platform functionality
                    </p>
                </SectionCard>

                {/* 9. Children's Privacy */}
                <SectionCard className="mb-5">
                    <h4>
                        <FaChild style={iconStyle} /> Children’s Privacy
                    </h4>
                    <p>
                        Our services are intended only for users 18 years and
                        older. If you suspect a minor has submitted information,
                        contact us for immediate deletion.
                    </p>
                </SectionCard>

                {/* 10. App Permissions */}
                <SectionCard className="mb-5">
                    <h4>
                        <FaMobileAlt style={iconStyle} /> Mobile App Permissions
                    </h4>
                    <ul>
                        <li>
                            <strong>Camera/Files:</strong> Upload
                            images/documents
                        </li>
                        <li>
                            <strong>Microphone:</strong>Enable Teleconsultations
                        </li>
                        <li>
                            <strong>Location:</strong> Show nearest labs/clinics
                        </li>
                        <li>
                            <strong>Phone:</strong> Allow direct call to support
                        </li>
                        <li>
                            <strong>Storage:</strong>Save reports and documents
                        </li>
                    </ul>
                    <p>Control permissions via your device settings.</p>
                </SectionCard>

                {/* 11. Cancellation & Refund */}
                <SectionCard
                    icon={<FaMoneyBillWave style={iconStyle} />}
                    title="Cancellation and Refund Policy"
                >
                    <h6>
                        a. Health Services (Checkups, Consultations, Lab Tests)
                    </h6>
                    <Table
                        headers={[
                            "Scenario",
                            "Cancellation Window",
                            "Refund Policy",
                        ]}
                        rows={[
                            [
                                "User cancels at least 24 hours before appointment",
                                "Minimum 24 hours prior",
                                "100% refund to original payment method within 5–7 working days",
                            ],
                            [
                                "User cancels within 12–24 hours of appointment",
                                "12–24 hours prior",
                                "50% refund to original payment method within 5–7 working days",
                            ],
                            [
                                "User cancels less than 12 hours before service time",
                                "Less than 12 hours prior",
                                "No refund applicable",
                            ],
                            [
                                "Service provider cancels or fails to deliver",
                                "Anytime",
                                "Full refund or rescheduling option offered to the user",
                            ],
                        ]}
                    />

                    <h6 className="mt-4">b. Membership Plans</h6>
                    <Table
                        headers={["Scenario", "Refund Window", "Refund Policy"]}
                        rows={[
                            [
                                "User cancels within 24 hours and no service used",
                                "Within 24 hours of payment",
                                "100% refund to original payment method within 5–7 working days",
                            ],
                            [
                                "User cancels after 24 hours or after using any service",
                                "After 24 hours or post usage",
                                "No refund will be provided under any circumstances",
                            ],
                        ]}
                    />

                    <h6 className="mt-4">
                        c. Returns and Consequences of Non-Acceptance
                    </h6>
                    <p>
                        As this is a digital healthcare platform, returns are
                        not applicable. If a user denies the delivery or
                        consultation of a service after booking and does not
                        respond within 24 hours, the service will be marked "No
                        Show" and no refund will be issued.
                    </p>
                </SectionCard>

                {/* 12. Updates */}
                <SectionCard className="mb-5">
                    <h4>
                        <FaSyncAlt style={iconStyle} /> Changes to This Privacy
                        Policies
                    </h4>
                    <p>
                        We may update this policy periodically. Material changes
                        will be posted on the Platform and may be communicated
                        via email. Continued use after changes constitutes
                        acceptance.
                    </p>
                </SectionCard>

                {/* 13. Contact */}
                <SectionCard className="mb-3">
                    <h4>
                        <FaEnvelope style={iconStyle} /> Contact Us
                    </h4>
                    <p>
                        Email:{" "}
                        <a href="mailto:support@gkcklinica.com">
                            support@gkcklinica.com
                        </a>
                    </p>
                    <p>
                        <FaPhone style={iconStyle} /> Phone: +91 97668 33510
                        (Mon–Fri, 9:00 AM – 5:00 PM IST)
                    </p>
                    <div style={{ lineHeight: "1.6" }}>
                        <p>
                            <strong>Registered Office:</strong><br />
                            402, Tirupati Enclave, Ramnagar Square, Nagpur - 440010
                        </p>

                        <p>
                            <strong>Overseas Office:</strong><br />
                            GKC Tech24 L.L.C, The Meydan Free Zone,<br />
                            Grandstand 6th Floor, Nad Al Sheba, Dubai (U.A.E)
                        </p>
                    </div>
                </SectionCard>
            </div>
        </Section>
    </>
    );
};

export default PrivacyPolicy;
